// Utilities
import {
  addDoc,
  collection,
  query,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  arrayRemove,
  arrayUnion,
  where,
  getDoc,
  getDocs,
  limit,
  documentId,
  type DocumentData,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useFirestore } from 'vuefire'
import { Datasource, DatasourceAtlassianProjectRef, DatasourceAtlassianSpaceRef, DatasourceConnectionRef, DatasourceFolder, DatasourceGoogleDriveMetadataDrive, DatasourceStatus, DatasourceType } from '@/models/Datasource'
import type { DatasourceConnection } from '@/models/DatasourceConnection'
import {
  oauthClientId,
  redirectToAtlassianAuthorize,
  redirectToGoogleAuthorize,
  redirectToOffice365Authorize,
} from '@/utils/datasourceOAuth'
import { useAppStore } from './app'
import { useAnalyticsStore } from './analytics'
import { useFeatureFlagsStore } from './featureFlags'
import { useProjectsStore } from './projects'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import {
  DatasourceSyncAction,
  FOLDER_DISCOVER_QUEUE,
  datasourceAuthTriggerField,
  datasourceSyncQueueId,
} from '@/constants/commandQueues'
import { logger } from '@/utils/logger'
import i18n from '@/plugins/i18n'

export const useDatasourcesStore = defineStore('datasources', {
  state: () => ({
    isLoading: false,
    loadedOnce: false,
    currentDatasourceId: '',
    datasources: [] as Datasource[],
    currentProjectId: '',
    unsubscribe: null as null | (() => void),
    // Folder management state
    foldersByParent: new Map<string, DatasourceFolder[]>(),
    foldersLoading: false,
    loadedFolderParents: new Set<string>(),
    // Short-lived parent-doc listeners for on-demand folder discovery
    discoveryUnsubscribes: new Map<string, () => void>(),
  }),
  getters: {
    currentDatasource: (state) => state.datasources.find((d) => d.id == state.currentDatasourceId),
    officeSites: (state): Map<string, any> => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      // Use Map to keep existing expectations in components
      return (ds?.office365_metadata?.sites as Map<string, any>) || new Map()
    },
    officeDrives: (state): Map<string, any> => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.office365_metadata?.drives as Map<string, any>) || new Map()
    },
    drivesEnabled: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.office365_configuration?.drives_enabled as string[]) || []
    },
    folderDiscoveryEnabled: (state): boolean => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.office365_configuration?.folder_discovery_enabled ?? true
    },
    folderFilteringEnabled: (state): boolean => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.office365_configuration?.folder_filtering_enabled ?? true
    },
    foldersEnabled: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.office365_configuration?.folders_enabled as string[]) || []
    },
    foldersExcluded: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.office365_configuration?.folders_excluded as string[]) || []
    },
    googleDrives: (state): Map<string, DatasourceGoogleDriveMetadataDrive> => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.google_metadata?.drives || new Map()
    },
    googleDrivesEnabled: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.google_configuration?.drives_enabled as string[]) || []
    },
    googleFolderDiscoveryEnabled: (state): boolean => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.google_configuration?.folder_discovery_enabled ?? false
    },
    googleFolderFilteringEnabled: (state): boolean => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.google_configuration?.folder_filtering_enabled ?? false
    },
    googleFoldersEnabled: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.google_configuration?.folders_enabled as string[]) || []
    },
    googleFoldersExcluded: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return (ds?.google_configuration?.folders_excluded as string[]) || []
    },
    // Atlassian
    atlassianJiraProjects: (state): DatasourceAtlassianProjectRef[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.atlassian_metadata?.jira_projects || []
    },
    atlassianConfluenceSpaces: (state): DatasourceAtlassianSpaceRef[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.atlassian_metadata?.confluence_spaces || []
    },
    atlassianSelectedJiraProjectIds: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.atlassian_configuration?.selected_jira_project_ids || []
    },
    atlassianSelectedConfluenceSpaceIds: (state): string[] => {
      const ds = state.datasources.find((d) => d.id == state.currentDatasourceId)
      return ds?.atlassian_configuration?.selected_confluence_space_ids || []
    },
    /** Datasources TraceX en état CONNECTED (utilisable pour la sync). */
    connectedTracexDatasources: (state): Datasource[] => {
      return state.datasources.filter(
        (d) => d.type === DatasourceType.TRACEX && d.status === DatasourceStatus.CONNECTED,
      )
    },
    getFolderById: (state) => (folderId: string): DatasourceFolder | undefined => {
      for (const folders of state.foldersByParent.values()) {
        const found = folders.find((f) => f.id === folderId)
        if (found) return found
      }
      return undefined
    },
  },
  actions: {
    /**
     * Follows one datasource doc until the caller stops it: a connection test
     * waits for the sync service's verdict there. Returns the stop function.
     */
    watchDatasourceDoc(
      pid: string,
      dsId: string,
      next: (data: DocumentData | undefined) => void,
      error: (e: unknown) => void,
    ): () => void {
      return onSnapshot(doc(useFirestore(), `projects/${pid}/datasources/${dsId}`), (snap) => next(snap.data()), error)
    },
    setCurrentDatasource(id: string) {
      if (id !== this.currentDatasourceId) {
        this.clearFoldersCache()
      }
      this.currentDatasourceId = id
    },
    // Office 365 OAuth
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async startOffice365OAuth(pid: string, name: string): Promise<boolean> {
      const clientId = oauthClientId(DatasourceType.OFFICE_365)
      if (!clientId) {
        logger.error('Missing VITE_MS_CLIENT_ID')
        return false
      }
      if (!pid) {
        logger.error('Missing pid for Office 365 delegated OAuth')
        return false
      }
      if (!name) {
        logger.error('Missing name for Office 365 delegated OAuth')
        return false
      }

      // Create placeholder datasource to avoid duplicates
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.OFFICE_365
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      if (!dsid) return false
      try { useAnalyticsStore().trackEvent('datasource_oauth_started', { provider: 'office365', pid }) } catch { }

      await redirectToOffice365Authorize({ kind: 'datasource', pid, dsid }, clientId)
      return true
    },

    async submitOffice365OAuth(
      project_id: string,
      datasource_id: string,
      code: string,
      code_verifier: string,
      redirect_uri: string,
      scopes: string[],
    ): Promise<void> {
      // Save the delegated OAuth credentials, then dispatch the AUTH action
      // (MYAC-773: command when the flag is on, else the legacy trigger flip).
      const update: Record<string, any> = {
        'office365_oauth.delegated_code': code,
        'office365_oauth.delegated_code_verifier': code_verifier,
        'office365_oauth.delegated_redirect_uri': redirect_uri,
      }
      if (scopes && scopes.length) {
        update['office365_oauth.scopes'] = scopes
      }
      try {
        await this.updateDatasource(project_id, datasource_id, update)
        await this._dispatchDatasourceSync(
          project_id,
          datasource_id,
          DatasourceSyncAction.AUTH,
          'trigger_office365_oauth',
          DatasourceType.OFFICE_365,
        )
        try { useAnalyticsStore().trackEvent('datasource_oauth_completed', { provider: 'office365', pid: project_id }) } catch { }
      } catch (e) {
        try { useAnalyticsStore().trackEvent('datasource_oauth_error', { provider: 'office365', pid: project_id, message: (e as Error)?.message }) } catch { }
        throw e
      }
      return
    },

    async resumeOffice365Setup(pid: string, ds: Datasource): Promise<void> {
      const clientId = oauthClientId(DatasourceType.OFFICE_365)
      if (!clientId) {
        logger.error('Missing VITE_MS_CLIENT_ID')
        return
      }
      if (!pid) {
        logger.error('Missing pid for Office 365 delegated OAuth')
        return
      }
      if (!ds) {
        logger.error('Missing ds for Office365')
        return
      }

      const dsid = ds.id
      if (!dsid) return

      await redirectToOffice365Authorize({ kind: 'datasource', pid, dsid }, clientId)
    },
    // Google Drive OAuth
    async resumeGoogleDriveOAuth(pid: string, ds: Datasource): Promise<void> {
      if (!pid || !ds?.id) return
      const clientId = oauthClientId(DatasourceType.GOOGLE_DRIVE)
      if (!clientId) {
        logger.error('Missing VITE_GOOGLE_CLIENT_ID')
        return
      }
      await redirectToGoogleAuthorize({ kind: 'datasource', pid, dsid: ds.id }, clientId)
    },

    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async startGoogleDriveOAuth(pid: string, name: string): Promise<boolean> {
      const clientId = oauthClientId(DatasourceType.GOOGLE_DRIVE)
      if (!clientId) {
        logger.error('Missing VITE_GOOGLE_CLIENT_ID')
        return false
      }
      if (!pid) {
        logger.error('No current project selected')
        return false
      }

      // Create placeholder datasource to avoid duplicates
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.GOOGLE_DRIVE
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      if (!dsid) return false
      try { useAnalyticsStore().trackEvent('datasource_oauth_started', { provider: 'google_drive', pid }) } catch { }

      await redirectToGoogleAuthorize({ kind: 'datasource', pid, dsid }, clientId)
      return true
    },
    async submitGoogleDriveOAuthCode(
      pid: string,
      datasourceId: string,
      payload: { code: string; code_verifier: string; redirect_uri: string; scopes?: string[] },
    ): Promise<void> {
      try {
        // Save the OAuth payload, then dispatch AUTH (MYAC-773: command when the
        // flag is on, else the legacy trigger_google_oauth flip).
        await this.updateDatasource(pid, datasourceId, { google_oauth: payload })
        await this._dispatchDatasourceSync(
          pid,
          datasourceId,
          DatasourceSyncAction.AUTH,
          'trigger_google_oauth',
          DatasourceType.GOOGLE_DRIVE,
        )
        try { useAnalyticsStore().trackEvent('datasource_oauth_completed', { provider: 'google_drive', pid }) } catch { }
      } catch (e) {
        try { useAnalyticsStore().trackEvent('datasource_oauth_error', { provider: 'google_drive', pid, message: (e as Error)?.message }) } catch { }
        throw e
      }
      return
    },
    // Atlassian OAuth (Jira + Confluence) — 3LO, no PKCE (confidential client,
    // backend exchanges with client_secret). Mirrors the Office 365 flow.
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async startAtlassianOAuth(pid: string, name: string): Promise<boolean> {
      const clientId = oauthClientId(DatasourceType.ATLASSIAN)
      if (!clientId) {
        logger.error('Missing VITE_ATLASSIAN_CLIENT_ID')
        return false
      }
      if (!pid || !name) {
        logger.error('Missing pid/name for Atlassian OAuth')
        return false
      }
      // Placeholder datasource to avoid duplicates and carry the dsid through OAuth.
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.ATLASSIAN
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      if (!dsid) return false
      try { useAnalyticsStore().trackEvent('datasource_oauth_started', { provider: 'atlassian', pid }) } catch { }
      redirectToAtlassianAuthorize({ kind: 'datasource', pid, dsid }, clientId)
      return true
    },

    async resumeAtlassianSetup(pid: string, ds: Datasource): Promise<void> {
      const clientId = oauthClientId(DatasourceType.ATLASSIAN)
      if (!clientId || !pid || !ds?.id) {
        logger.error('Missing data for Atlassian resume')
        return
      }
      redirectToAtlassianAuthorize({ kind: 'datasource', pid, dsid: ds.id }, clientId)
    },

    async submitAtlassianOAuth(
      pid: string,
      datasource_id: string,
      code: string,
      redirect_uri: string,
      scopes: string[],
    ): Promise<void> {
      // Save the OAuth credentials, then dispatch the AUTH action (MYAC-773:
      // command when the flag is on, else the legacy trigger flip).
      const update: Record<string, any> = {
        'atlassian_oauth.code': code,
        'atlassian_oauth.redirect_uri': redirect_uri,
        'atlassian_oauth.scopes': scopes,
      }
      try {
        await this.updateDatasource(pid, datasource_id, update)
        await this._dispatchDatasourceSync(
          pid,
          datasource_id,
          DatasourceSyncAction.AUTH,
          'trigger_atlassian_oauth',
          DatasourceType.ATLASSIAN,
        )
        try { useAnalyticsStore().trackEvent('datasource_oauth_completed', { provider: 'atlassian', pid }) } catch { }
      } catch (e) {
        try { useAnalyticsStore().trackEvent('datasource_oauth_error', { provider: 'atlassian', pid, message: (e as Error)?.message }) } catch { }
        throw e
      }
    },

    async setAtlassianSelectedJiraProjects(pid: string, datasourceId: string, ids: string[]): Promise<void> {
      await this.updateDatasource(pid, datasourceId, { 'atlassian_configuration.selected_jira_project_ids': ids })
    },
    async setAtlassianSelectedConfluenceSpaces(pid: string, datasourceId: string, ids: string[]): Promise<void> {
      await this.updateDatasource(pid, datasourceId, { 'atlassian_configuration.selected_confluence_space_ids': ids })
    },
    /** Save both selections at once (single Firestore write) — used by the selection modal.
     *  Jira & Confluence are always enabled (no per-product toggle), so re-affirm both. */
    async setAtlassianSelection(pid: string, datasourceId: string, jiraIds: string[], confluenceIds: string[]): Promise<void> {
      await this.updateDatasource(pid, datasourceId, {
        'atlassian_configuration.selected_jira_project_ids': jiraIds,
        'atlassian_configuration.selected_confluence_space_ids': confluenceIds,
        'atlassian_configuration.sync_jira': true,
        'atlassian_configuration.sync_confluence': true,
      })
    },
    async setAtlassianSyncFlag(
      pid: string,
      datasourceId: string,
      flag: 'sync_jira' | 'sync_confluence' | 'sync_attachments' | 'sync_comments',
      value: boolean,
    ): Promise<void> {
      await this.updateDatasource(pid, datasourceId, { [`atlassian_configuration.${flag}`]: value })
    },

    // TraceX — shell only (name + type); workspace and credentials are set on the datasource detail page.
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async createTracexDatasource(pid: string, name: string): Promise<string | null> {
      if (!pid || !name) {
        logger.error('Missing required fields for TraceX datasource')
        return null
      }
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.TRACEX
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      return dsid
    },

    // Qualishare — shell only (name + type); identifiants sur la fiche de la source de données.
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async createQualishareDatasource(pid: string, name: string): Promise<string | null> {
      if (!pid || !name) {
        logger.error('Missing required fields for Qualishare datasource')
        return null
      }
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.QUALISHARE
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      return dsid
    },

    // Odoo — shell only (name + type); URL, base, identifiants et modules sont
    // saisis sur la fiche de la source de données après création.
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async createOdooDatasource(pid: string, name: string): Promise<string | null> {
      if (!pid || !name) {
        logger.error('Missing required fields for Odoo datasource')
        return null
      }
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = DatasourceType.ODOO
      placeholder.name = name
      const dsid = await this.createDatasource(pid, placeholder)
      return dsid
    },

    /**
     * MYAC-1515 — creates a datasource that signs in through a connection of
     * the company: no redirect, no credentials form. The pointer is written at
     * creation (the only moment the rules allow it), then the connector's
     * service is asked to authenticate — it finds the connection, sets the
     * datasource `connected` and starts the first metadata sync. One action so
     * every entry point creates and asks in that order.
     *
     * Only a CONNECTED connection of the QMS / product's OWN company is used:
     * the rules refuse another company's, and the backend attaches nothing to
     * a connection that is not connected.
     */
    // csm-event: none — delegates to createDatasource, which emits datasource_connected
    async createDatasourceFromConnection(
      pid: string,
      name: string,
      connection: Pick<DatasourceConnection, 'id' | 'type' | 'tenant_id' | 'status'>,
    ): Promise<string | null> {
      const triggerField = connection?.type ? datasourceAuthTriggerField(connection.type) : null
      if (!pid || !name || !connection?.id || !connection.tenant_id || !triggerField) {
        logger.error('Missing required fields to create a datasource from a company connection')
        return null
      }
      if (connection.status !== DatasourceStatus.CONNECTED) {
        logger.error('Datasource not created: its company connection is not connected')
        return null
      }
      const projectTenantId = useProjectsStore().projects.find((p) => p.id === pid)?.tenant?.id
      if (!projectTenantId || projectTenantId !== connection.tenant_id) {
        logger.error('Datasource not created: the connection is not one of the company this QMS / product belongs to')
        return null
      }
      const placeholder = new Datasource()
      placeholder.status = DatasourceStatus.PENDING
      placeholder.type = connection.type
      placeholder.name = name
      placeholder.connection = new DatasourceConnectionRef()
      placeholder.connection.tenant_id = connection.tenant_id
      placeholder.connection.connection_id = connection.id
      const dsid = await this.createDatasource(pid, placeholder)
      if (!dsid) return null
      try {
        await this._dispatchDatasourceSync(pid, dsid, DatasourceSyncAction.AUTH, triggerField, connection.type)
      } catch (error) {
        // The datasource exists, pending: its page offers to ask again
        // (`retryConnectionSignIn`), so the id is returned all the same.
        logger.error('The sign-in of a datasource on a company connection could not be asked for', error)
      }
      return dsid
    },

    /**
     * MYAC-1515 — asks again for the sign-in of a datasource that uses a
     * company connection, when the first request did not attach it: it stayed
     * `pending`, or ended in `error`. The very request sent at creation — the
     * backend finds the connection again. Resolves true once the request is
     * written; what the backend then did shows on the datasource's status,
     * never in a toast from here.
     */
    // csm-event: none — repeats the request of createDatasourceFromConnection, whose create emitted datasource_connected
    async retryConnectionSignIn(
      pid: string,
      datasource: Pick<Datasource, 'id' | 'type' | 'status' | 'connection' | 'locked'>,
    ): Promise<boolean> {
      const triggerField = datasource?.type ? datasourceAuthTriggerField(datasource.type) : null
      const attachFailed =
        datasource?.status === DatasourceStatus.PENDING || datasource?.status === DatasourceStatus.ERROR
      if (!pid || !datasource?.id || !datasource.connection || !triggerField || !attachFailed || datasource.locked) {
        logger.error('Sign-in not asked again: not a datasource on a company connection waiting to be attached')
        return false
      }
      let sent = false
      try {
        sent = await this._dispatchDatasourceSync(pid, datasource.id, DatasourceSyncAction.AUTH, triggerField, datasource.type)
      } catch (error) {
        logger.error('The sign-in of a datasource on a company connection could not be asked again', error)
      }
      if (!sent) {
        useAppStore().toast(
          i18n.global.t('datasource.connections.title'),
          i18n.global.t('datasource.connections.attach.retry_not_sent'),
          'error',
          7000,
        )
      }
      return sent
    },

    /**
     * MYAC-773 dual-run dispatch for datasource actions. When the command-queue
     * producer flag is on AND the datasource type has a queue, enqueue a
     * `datasource_sync` command; otherwise (flag off, or an out-of-scope type
     * like manual_upload) fall back to the legacy `trigger_*` field flip. The UI
     * observes the same status/progress either way.
     *
     * `knownType` is for callers that run before the datasource list is loaded
     * — the OAuth callback pages open on a fresh page load after the provider's
     * redirect, so the store lookup finds nothing there (MYAC-1532).
     *
     * Resolves true when the request was written. The command path throws on a
     * refused write; the legacy path resolves false, `updateDatasource` having
     * said so itself (and anything but that `false` counts as written).
     */
    async _dispatchDatasourceSync(
      pid: string,
      datasourceId: string,
      action: DatasourceSyncAction,
      triggerField: string,
      knownType?: DatasourceType,
    ): Promise<boolean> {
      const type = knownType ?? this.datasources.find((d) => d.id === datasourceId)?.type
      const queueId = type ? datasourceSyncQueueId(type) : null
      const producersEnabled = useFeatureFlagsStore().isCommandQueueProducersEnabled
      if (producersEnabled && type && queueId) {
        const db = useFirestore()
        await addDoc(
          collection(db, `projects/${pid}/command_queues/${queueId}/commands`),
          {
            status: 'requested',
            payload: {
              kind: 'datasource_sync',
              datasource_id: datasourceId,
              datasource_type: type,
              action,
            },
            requested_by: null,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          },
        )
        return true
      }
      if (producersEnabled && !type) {
        // Not silent: with the flag on, a legacy write is an unmigrated caller.
        logger.warn(
          `[datasources] ${action} for an unknown datasource falls back to the legacy ${triggerField} trigger`,
        )
      }
      return (await this.updateDatasource(pid, datasourceId, { [triggerField]: 'requested' })) !== false
    },

    /**
     * MYAC-773 dual-run dispatch for on-demand folder-tree expansion. Command
     * path writes a `folder_discover` command; legacy path flips
     * `trigger_discover_children` on the folder doc.
     */
    async _dispatchFolderDiscover(
      pid: string,
      datasourceId: string,
      folderId: string,
      folderRef: ReturnType<typeof doc>,
    ): Promise<void> {
      if (useFeatureFlagsStore().isCommandQueueProducersEnabled) {
        const db = useFirestore()
        await addDoc(
          collection(db, `projects/${pid}/command_queues/${FOLDER_DISCOVER_QUEUE}/commands`),
          {
            status: 'requested',
            payload: {
              kind: 'folder_discover',
              datasource_id: datasourceId,
              folder_id: folderId,
            },
            requested_by: null,
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
          },
        )
        return
      }
      await updateDoc(folderRef, { trigger_discover_children: 'requested' })
    },

    // Sync triggers
    async triggerFullSync(pid: string, datasourceId: string): Promise<void> {
      const ds = this.datasources.find((d) => d.id === datasourceId)
      if (
        ds?.type === DatasourceType.TRACEX &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Full sync ignored: TraceX datasource is not connected',
        )
        return
      }
      if (
        ds?.type === DatasourceType.QUALISHARE &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Full sync ignored: Qualishare datasource is not connected',
        )
        return
      }
      if (
        ds?.type === DatasourceType.ODOO &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Full sync ignored: Odoo datasource is not connected',
        )
        return
      }
      try {
        await this._dispatchDatasourceSync(
          pid,
          datasourceId,
          DatasourceSyncAction.FULL_SYNC,
          'trigger_full_sync',
        )
        try { useAnalyticsStore().trackEvent('datasource_sync_triggered', { type: 'full' }) } catch { }
        try { trackAction(CsmAction.DatasourceResynced, { projectId: pid }) } catch { }
      } catch (e) {
        throw e
      }
    },
    async triggerDeltaSync(pid: string, datasourceId: string): Promise<void> {
      const ds = this.datasources.find((d) => d.id === datasourceId)
      if (
        ds?.type === DatasourceType.TRACEX &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Delta sync ignored: TraceX datasource is not connected',
        )
        return
      }
      if (
        ds?.type === DatasourceType.QUALISHARE &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Delta sync ignored: Qualishare datasource is not connected',
        )
        return
      }
      if (
        ds?.type === DatasourceType.ODOO &&
        ds.status !== DatasourceStatus.CONNECTED
      ) {
        logger.warn(
          '[datasources] Delta sync ignored: Odoo datasource is not connected',
        )
        return
      }
      try {
        await this._dispatchDatasourceSync(
          pid,
          datasourceId,
          DatasourceSyncAction.DELTA_SYNC,
          'trigger_delta_sync',
        )
        try { useAnalyticsStore().trackEvent('datasource_sync_triggered', { type: 'delta' }) } catch { }
        try { trackAction(CsmAction.DatasourceResynced, { projectId: pid }) } catch { }
      } catch (e) {
        throw e
      }
    },
    async triggerDelete(pid: string, datasourceId: string): Promise<void> {
      await this._dispatchDatasourceSync(
        pid,
        datasourceId,
        DatasourceSyncAction.DELETE,
        'trigger_delete',
      )
      try { trackAction(CsmAction.DatasourceDeleted, { projectId: pid }) } catch { }
    },
    /**
     * Demande à `tracex-sync` de lister les projets Huly du workspace.
     * Le backend remplit `tracex_metadata.projects` que le front observe via
     * la souscription `onSnapshot` déjà active sur les datasources.
     */
    async triggerTracexListProjects(pid: string, datasourceId: string): Promise<void> {
      const ds = this.datasources.find((d) => d.id === datasourceId)
      if (ds?.type !== DatasourceType.TRACEX) {
        logger.warn('[datasources] triggerTracexListProjects ignored: datasource is not TraceX')
        return
      }
      if (ds.status !== DatasourceStatus.CONNECTED) {
        logger.warn('[datasources] triggerTracexListProjects ignored: datasource is not connected')
        return
      }
      await this._dispatchDatasourceSync(
        pid,
        datasourceId,
        DatasourceSyncAction.LIST_PROJECTS,
        'trigger_tracex_list_projects',
      )
    },
    async triggerMetadataUpdate(pid: string, datasourceId: string): Promise<void> {
      try {
        await this._dispatchDatasourceSync(
          pid,
          datasourceId,
          DatasourceSyncAction.METADATA_SYNC,
          'trigger_sites_metadata_sync',
        )
        try { useAnalyticsStore().trackEvent('datasource_metadata_update_triggered', { dsid: datasourceId }) } catch { }
      } catch (e) {
        throw e
      }
    },
    // Datasource management
    createDatasource: async function (
      pid: string,
      datasource: Datasource,
    ): Promise<string | null> {
      this.isLoading = true
      let docid = null
      try {
        const db = useFirestore()
        const ref = collection(db, `projects/${pid}/datasources/`)

        // Firestore rules allow create only with exactly these keys (createFieldsAllow).
        const payload: Record<string, unknown> = {
          name: datasource.name,
          type: datasource.type,
          status: datasource.status,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        // MYAC-1515 — plus the pointer to a company connection, and only when
        // there is one: a datasource with its own sign-in is created as before.
        // Never on a manual upload, which signs in to nothing (the rules refuse it).
        if (datasource.connection && datasource.type !== DatasourceType.MANUAL_UPLOAD) {
          payload.connection = {
            tenant_id: datasource.connection.tenant_id,
            connection_id: datasource.connection.connection_id,
          }
        }
        logger.debug(payload)

        const doc = await addDoc(ref, payload)
        docid = doc.id
        try { useAnalyticsStore().trackEvent('datasource_create_success', { type: datasource.type }) } catch { }
        try { trackAction(CsmAction.DatasourceConnected, { projectId: pid }) } catch { }
      } catch (error) {
        useAppStore().toast('Error creating datasource', error as string, 'error', 5000)
        logger.error('Error creating datasource', error)
        try { useAnalyticsStore().trackEvent('datasource_create_error', { type: datasource.type, message: (error as Error)?.message }) } catch { }
      } finally {
      }
      return docid
    },
    /** Resolves true when the write went through, false when it was refused (said in a toast here). */
    updateDatasource: async function (
      pid: string,
      datasourceId: string,
      payload: { [key: string]: any },
    ): Promise<boolean> {
      this.isLoading = true
      try {
        const db = useFirestore()
        const ref = doc(db, `projects/${pid}/datasources/${datasourceId}`)
        const updatePayload = {
          ...payload,
          updated_at: serverTimestamp(),
        }
        logger.debug(ref.path)
        logger.info(updatePayload)
        await updateDoc(ref, updatePayload)
        return true
      } catch (error) {
        logger.error('Error updating datasource', error)
        useAppStore().toast('Error updating datasource', error as string, 'error', 5000)
        return false
      } finally {
        this.isLoading = false
      }
    },
    async listDatasources(pid: string, forceUpdate = false) {
      try {
        if (!pid) {
          logger.error('Missing pid for listing datasources')
          return Promise.resolve()
        }

        // Reuse existing subscription if same project and no force update
        if (this.unsubscribe && this.currentProjectId === pid && !forceUpdate) {
          logger.debug('Reusing existing datasources subscription')
          return Promise.resolve()
        }

        // Clean up previous subscription if switching projects or forcing update
        if (this.unsubscribe) {
          try {
            this.unsubscribe()
          } catch (_) {
            /* ignore */
          }
          this.unsubscribe = null
        }

        this.isLoading = true
        this.currentProjectId = pid

        const db = useFirestore()
        const q = query(collection(db, `projects/${pid}/datasources`))

        this.unsubscribe = onSnapshot(
          q,
          (r) => {
            this.datasources = r.docs
              .map((p) => Datasource.fromFirebase(p))
              .filter((p) => p !== null)
              .filter((p) => p.status !== DatasourceStatus.TO_DELETE)
            if (this.currentDatasourceId && !this.datasources.find(d => d.id === this.currentDatasourceId)) {
              this.currentDatasourceId = ''
            }
            if (this.currentDatasourceId == '' && this.datasources.length) {
              this.currentDatasourceId = this.datasources[0].id
            }
            this.loadedOnce = true
            this.isLoading = false
          },
          (error) => {
            logger.error('Error getting datasources', error)
            this.isLoading = false
          },
        )
      } catch (error) {
        logger.error('Error getting datasources', error)
        this.isLoading = false
      }
    },
    /**
     * One datasource: the loaded list when it holds this project's, else one
     * read by path. `null` when it does not exist; a failed read throws after
     * logging, like `documents.getDocument`.
     */
    async getDatasource(pid: string, dsid: string): Promise<Datasource | null> {
      if (!pid || !dsid) return null
      if (this.currentProjectId === pid) {
        const loaded = this.datasources.find((d) => d.id === dsid)
        if (loaded) return loaded
      }
      try {
        return Datasource.fromFirebase(await getDoc(doc(useFirestore(), `projects/${pid}/datasources/${dsid}`)))
      } catch (e) {
        logger.error('Error reading datasource', pid, dsid, e)
        throw e
      }
    },
    async enableDrive(pid: string, datasourceId: string, driveId: string, configPrefix = 'office365_configuration'): Promise<boolean> {
      logger.info('enableDrive', pid, datasourceId, driveId, configPrefix)
      const res = await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.drives_enabled`]: arrayUnion(driveId),
      })
      try { useAnalyticsStore().trackEvent('drive_enable', { dsid: datasourceId }) } catch { }
      return res
    },
    async disableDrive(pid: string, datasourceId: string, driveId: string, configPrefix = 'office365_configuration'): Promise<boolean> {
      logger.info('disableDrive', pid, datasourceId, driveId, configPrefix)
      const res = await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.drives_enabled`]: arrayRemove(driveId),
      })
      try { useAnalyticsStore().trackEvent('drive_disable', { dsid: datasourceId }) } catch { }
      return res
    },
    // Folder management actions
    async loadRootFolders(pid: string, datasourceId: string, driveId: string): Promise<DatasourceFolder[]> {
      if (!pid || !datasourceId || !driveId) {
        logger.error('Missing parameters for loadRootFolders')
        return []
      }

      const cacheKey = `root:${driveId}`
      if (this.loadedFolderParents.has(cacheKey)) {
        return this.foldersByParent.get(cacheKey) || []
      }

      this.foldersLoading = true
      try {
        const db = useFirestore()
        const foldersRef = collection(db, DatasourceFolder.collectionPath(pid, datasourceId))
        const q = query(
          foldersRef,
          where('drive_id', '==', driveId),
          where('is_root', '==', true),
        )
        const snapshot = await getDocs(q)
        const folders = snapshot.docs
          .map((doc) => DatasourceFolder.fromFirebase(doc))
          .filter((f): f is DatasourceFolder => f !== null)
          .sort((a, b) => a.folder_name.localeCompare(b.folder_name))

        this.foldersByParent.set(cacheKey, folders)
        this.loadedFolderParents.add(cacheKey)
        return folders
      } catch (error) {
        logger.error('Error loading root folders', error)
        useAppStore().toast('Error loading folders', (error as Error).message, 'error', 5000)
        return []
      } finally {
        this.foldersLoading = false
      }
    },
    async loadChildFolders(pid: string, datasourceId: string, parentFolderId: string): Promise<DatasourceFolder[]> {
      if (!pid || !datasourceId || !parentFolderId) {
        logger.error('Missing parameters for loadChildFolders')
        return []
      }

      const cacheKey = `parent:${parentFolderId}`
      if (this.loadedFolderParents.has(cacheKey)) {
        return this.foldersByParent.get(cacheKey) || []
      }

      this.foldersLoading = true
      try {
        const db = useFirestore()
        const foldersRef = collection(db, DatasourceFolder.collectionPath(pid, datasourceId))
        const q = query(
          foldersRef,
          where('parent_folder_id', '==', parentFolderId),
        )
        const snapshot = await getDocs(q)
        const folders = snapshot.docs
          .map((doc) => DatasourceFolder.fromFirebase(doc))
          .filter((f): f is DatasourceFolder => f !== null)
          .sort((a, b) => a.folder_name.localeCompare(b.folder_name))

        this.foldersByParent.set(cacheKey, folders)
        this.loadedFolderParents.add(cacheKey)
        return folders
      } catch (error) {
        logger.error('Error loading child folders', error)
        useAppStore().toast('Error loading folders', (error as Error).message, 'error', 5000)
        return []
      } finally {
        this.foldersLoading = false
      }
    },
    async loadFoldersByIds(pid: string, datasourceId: string, folderIds: string[]): Promise<DatasourceFolder[]> {
      if (!pid || !datasourceId || folderIds.length === 0) {
        return []
      }

      // Filter out any undefined, null, or empty string values
      const validFolderIds = folderIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      if (validFolderIds.length === 0) {
        return []
      }

      this.foldersLoading = true
      try {
        const db = useFirestore()
        const foldersRef = collection(db, DatasourceFolder.collectionPath(pid, datasourceId))
        const allFolders: DatasourceFolder[] = []

        // Firestore 'in' operator supports max 30 values per query
        const CHUNK_SIZE = 30
        for (let i = 0; i < validFolderIds.length; i += CHUNK_SIZE) {
          const chunk = validFolderIds.slice(i, i + CHUNK_SIZE)
          const q = query(foldersRef, where(documentId(), 'in', chunk))
          const snapshot = await getDocs(q)
          const folders = snapshot.docs
            .map((doc) => DatasourceFolder.fromFirebase(doc))
            .filter((f): f is DatasourceFolder => f !== null)
          allFolders.push(...folders)
        }

        return allFolders
      } catch (error) {
        logger.error('Error loading folders by IDs', error)
        useAppStore().toast('Error loading folders', (error as Error).message, 'error', 5000)
        return []
      } finally {
        this.foldersLoading = false
      }
    },
    clearFoldersCache() {
      this.cancelAllDiscoveryWaits()
      this.foldersByParent.clear()
      this.loadedFolderParents.clear()
    },
    async triggerDiscoverChildren(pid: string, datasourceId: string, folderId: string): Promise<void> {
      if (!pid || !datasourceId || !folderId) {
        logger.error('Missing parameters for triggerDiscoverChildren')
        return
      }
      try {
        const db = useFirestore()
        const folderRef = doc(db, `${DatasourceFolder.collectionPath(pid, datasourceId)}/${folderId}`)
        await this._dispatchFolderDiscover(pid, datasourceId, folderId, folderRef)
      } catch (error) {
        logger.error('Error triggering folder discovery', error)
        useAppStore().toast('Error triggering folder discovery', (error as Error).message, 'error', 5000)
        throw error
      }
    },
    waitForDiscovery(pid: string, datasourceId: string, folderId: string): Promise<boolean> {
      if (!pid || !datasourceId || !folderId) {
        logger.error('Missing parameters for waitForDiscovery')
        return Promise.resolve(false)
      }

      // Clean up any existing listener for this folder
      this.cancelDiscoveryWait(folderId)

      const TIMEOUT_MS = 30_000
      const db = useFirestore()
      const folderRef = doc(db, `${DatasourceFolder.collectionPath(pid, datasourceId)}/${folderId}`)

      return new Promise<boolean>((resolve) => {
        let resolved = false
        // The backend marks the folder `is_discovering_children` (and latches the
        // trigger) when it picks the work up, on both the legacy and the command
        // path. Until that is seen, an idle folder is not a failed discovery —
        // it is one that has not started. MYAC-1125: on the command path the
        // front never writes to the folder, so the very first snapshot is that
        // idle state and used to resolve false ~200ms after the click, leaving
        // the folder tree empty while the backend was still discovering.
        let discoveryStarted = false
        const cleanup = () => {
          if (!resolved) {
            resolved = true
            this.discoveryUnsubscribes.delete(folderId)
          }
        }

        const timer = setTimeout(() => {
          logger.warn(`Discovery timeout for folder ${folderId}`)
          const unsub = this.discoveryUnsubscribes.get(folderId)
          if (unsub) unsub()
          cleanup()
          resolve(false)
        }, TIMEOUT_MS)

        const unsubscribe = onSnapshot(
          folderRef,
          (snapshot) => {
            if (resolved) return
            const data = snapshot.data()
            if (!data) return

            if (data.is_discovering_children === true ||
                data.trigger_discover_children === 'requested' ||
                data.trigger_discover_children === 'scheduled') {
              discoveryStarted = true
            }

            if (data.children_discovered === true) {
              clearTimeout(timer)
              unsubscribe()
              cleanup()
              resolve(true)
              return
            }

            // Discovery failed: backend reset is_discovering without setting children_discovered
            if (discoveryStarted &&
                data.is_discovering_children === false &&
                data.trigger_discover_children !== 'requested' &&
                data.children_discovered === false) {
              clearTimeout(timer)
              unsubscribe()
              cleanup()
              resolve(false)
            }
          },
          (error) => {
            logger.error('Error watching folder discovery', error)
            clearTimeout(timer)
            unsubscribe()
            cleanup()
            resolve(false)
          },
        )

        this.discoveryUnsubscribes.set(folderId, () => {
          clearTimeout(timer)
          unsubscribe()
        })
      })
    },
    cancelDiscoveryWait(folderId: string) {
      const unsub = this.discoveryUnsubscribes.get(folderId)
      if (unsub) {
        unsub()
        this.discoveryUnsubscribes.delete(folderId)
      }
    },
    cancelAllDiscoveryWaits() {
      for (const unsub of this.discoveryUnsubscribes.values()) {
        unsub()
      }
      this.discoveryUnsubscribes.clear()
    },
    async setFolderDiscoveryEnabled(pid: string, datasourceId: string, enabled: boolean, configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('setFolderDiscoveryEnabled', pid, datasourceId, enabled, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folder_discovery_enabled`]: enabled,
      })
      try { useAnalyticsStore().trackEvent('folder_discovery_toggle', { dsid: datasourceId, enabled }) } catch { }
    },
    async setFolderFilteringEnabled(pid: string, datasourceId: string, enabled: boolean, configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('setFolderFilteringEnabled', pid, datasourceId, enabled, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folder_filtering_enabled`]: enabled,
      })
      try { useAnalyticsStore().trackEvent('folder_filtering_toggle', { dsid: datasourceId, enabled }) } catch { }
    },
    async enableFolder(pid: string, datasourceId: string, folderId: string, configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('enableFolder', pid, datasourceId, folderId, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folders_enabled`]: arrayUnion(folderId),
      })
      try { useAnalyticsStore().trackEvent('folder_enable', { dsid: datasourceId }) } catch { }
    },
    async disableFolder(pid: string, datasourceId: string, folderId: string, configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('disableFolder', pid, datasourceId, folderId, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folders_enabled`]: arrayRemove(folderId),
      })
      try { useAnalyticsStore().trackEvent('folder_disable', { dsid: datasourceId }) } catch { }
    },
    async updateFoldersEnabled(pid: string, datasourceId: string, folderIds: string[], configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('updateFoldersEnabled', pid, datasourceId, folderIds, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folders_enabled`]: folderIds,
      })
      try { useAnalyticsStore().trackEvent('folders_enabled_update', { dsid: datasourceId, count: folderIds.length }) } catch { }
    },
    async updateFoldersExcluded(pid: string, datasourceId: string, folderIds: string[], configPrefix = 'office365_configuration'): Promise<void> {
      logger.info('updateFoldersExcluded', pid, datasourceId, folderIds, configPrefix)
      await this.updateDatasource(pid, datasourceId, {
        [`${configPrefix}.folders_excluded`]: folderIds,
      })
      try { useAnalyticsStore().trackEvent('folders_excluded_update', { dsid: datasourceId, count: folderIds.length }) } catch { }
    },
  },
})
